/**
 * Instrumentacao de comportamento do Turbine: Google Tag Manager exclusivo
 * (GTM-T5JRR5RC) que carrega o Microsoft Clarity do projeto Personalizacao.
 *
 * Regime best-effort: qualquer falha aqui (GTM bloqueado, storage negado,
 * Clarity fora do ar) e engolida. Nada neste arquivo pode afetar o
 * personalizador. Em desenvolvimento, `window.__turbineAnalytics` mostra o
 * estado e os ultimos eventos para diagnostico da propria integracao.
 *
 * Fluxo:
 *  1. `initAnalytics()` (main.tsx) decide pelo documento (rota publica, fora do
 *     modo render, nao automacao). Fora disso, nada e carregado.
 *  2. Consentimento comeca NEGADO (GTM Consent Mode + Clarity consentv2): o
 *     Clarity grava sem cookies. So uma mensagem `turbine:consent` da loja
 *     (origem verificada, `source === window.parent`) concede.
 *  3. O GTM e injetado depois do `load` do documento, em idle, para nao
 *     disputar banda/CPU com o payload, os chunks e os GLB do personalizador.
 *     Eventos e tags anteriores ficam na fila (dataLayer e `clarity.q`).
 */
import {
  decideAnalytics,
  surfaceFor,
  environmentFor,
  entryFor,
  deviceFor,
  isTrackablePath,
  isConsentOrigin,
  parseConsentMessage,
  mapTurbineEvent,
  sanitizeProps,
  errorCategoryForMonitorMarker,
  ONCE_PER_DOCUMENT,
  SKIP_FIRST_PER_DOCUMENT,
  GTM_MODE_MARK,
  GTM_FALLBACK_MS,
  MAX_BUFFERED_EVENTS,
  resolveGtmLoadMode,
  consentRequired,
  type GtmLoadMode,
  type ConsentState,
} from './policy';

type Clarity = ((...args: unknown[]) => void) & { q?: unknown[][] };

interface AnalyticsWindow extends Window {
  dataLayer?: unknown[];
  clarity?: Clarity;
  __turbineAnalytics?: AnalyticsState;
}

interface AnalyticsState {
  enabled: boolean;
  reason: string;
  gtmId: string;
  gtmRequested: boolean;
  gtmLoaded: boolean;
  gtmFailed: boolean;
  gtmMode: GtmLoadMode;
  consentRequired: boolean;
  gtmReleased: boolean;
  gtmInjectedAt: number | null;
  buffered: number;
  dropped: number;
  stopped: boolean;
  consentRevoked: boolean;
  consent: ConsentState | null;
  surface: string;
  last: Array<{ name: string; t: number }>;
}

const w = (typeof window !== 'undefined' ? window : undefined) as AnalyticsWindow | undefined;
const fired = new Set<string>();
const errorCounts = new Map<string, number>();
const MAX_ERRORS_PER_CATEGORY = 3;
let state: AnalyticsState | null = null;
let pendingGtm: { gtmId: string; mark: string; timer: number | null } | null = null;
let releasedGtmId: string | null = null;
let injectScheduled = false;

function safe(fn: () => void): void {
  try { fn(); } catch (err) {
    if (import.meta.env?.DEV) console.debug('[analytics]', err);
  }
}

function clarity(...args: unknown[]): void {
  if (!w || !state?.enabled || state.stopped || state.consentRevoked) return;
  safe(() => { w.clarity?.(...args); });
}

function gtag(..._args: unknown[]): void {
  // Formato exigido pelo GTM para comandos de consentimento: `arguments`.
  // eslint-disable-next-line prefer-rest-params
  if (w) (w.dataLayer = w.dataLayer || []).push(arguments);
}

function embedded(): boolean {
  try { return !!w && w.parent !== w; } catch { return true; }
}

function parentIsTurbine(): boolean {
  if (!w || !embedded()) return false;
  try {
    // Mesma origem => leitura permitida => pai e o proprio Turbine (catalogo/grupo).
    return w.parent.location.origin === w.location.origin;
  } catch {
    return false;
  }
}

function injectGtm(gtmId: string): void {
  if (!w || !state || state.gtmRequested) return;
  // Protecao contra inicializacao dupla no MESMO documento.
  if (document.querySelector(`script[data-turbine-gtm="${gtmId}"]`)) return;
  state.gtmRequested = true;
  state.gtmInjectedAt = Math.round(performance.now());
  safe(() => {
    (w.dataLayer = w.dataLayer || []).push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
    s.setAttribute('data-turbine-gtm', gtmId);
    s.onload = () => { if (state) state.gtmLoaded = true; };
    s.onerror = () => { if (state) state.gtmFailed = true; };
    document.head.appendChild(s);
  });
}

/** Entrega o GTM ao navegador em idle (nunca no meio de uma tarefa de renderizacao). */
function injectWhenIdle(gtmId: string): void {
  if (!w || injectScheduled) return;
  injectScheduled = true;
  const ric = (w as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
  if (typeof ric === 'function') ric(() => injectGtm(gtmId), { timeout: 2000 });
  else w.setTimeout(() => injectGtm(gtmId), 1000);
}

/**
 * O GTM so e injetado quando DUAS condicoes valem: o momento certo chegou
 * (marco de performance ou fallback) e a loja concedeu analytics. A ordem em que
 * isso acontece nao importa.
 */
function maybeInject(): void {
  if (!state || !releasedGtmId || state.gtmRequested || state.stopped || state.consentRevoked) return;
  if (state.consentRequired && state.consent?.analytics !== true) return;
  injectWhenIdle(releasedGtmId);
}

/** O momento de carregar chegou (nao e autorizacao de coleta). */
function releaseGtm(gtmId: string): void {
  releasedGtmId = gtmId;
  if (state) {
    state.gtmReleased = true;
    if (state.consentRequired && state.consent === null) requestConsent();
  }
  maybeInject();
}

/**
 * Modo 'load': apos o `load` do documento, em idle (catalogos, sem cena 3D).
 * Modos 'settled'/'interactive': espera o marco de performance do viewer; se ele
 * nunca ocorrer, o GTM entra apos GTM_FALLBACK_MS (sessoes com erro ou sem modelo
 * continuam gravadas). Nenhum modo antecipa a coleta: o consentimento ja esta
 * negado ate a loja conceder.
 */
function scheduleGtm(gtmId: string, mode: GtmLoadMode): void {
  if (!w) return;
  if (mode === 'load') {
    const go = () => releaseGtm(gtmId);
    if (document.readyState === 'complete') go();
    else w.addEventListener('load', go, { once: true });
    return;
  }
  pendingGtm = { gtmId, mark: GTM_MODE_MARK[mode], timer: null };
  pendingGtm.timer = w.setTimeout(() => {
    if (!pendingGtm) return;
    const id = pendingGtm.gtmId;
    pendingGtm = null;
    releaseGtm(id);
  }, GTM_FALLBACK_MS) as unknown as number;
}

/** Chamado por perf/marks.ts a cada marco novo: libera o GTM quando e o marco esperado. */
export function onPerfMark(name: string): void {
  if (!pendingGtm || pendingGtm.mark !== name) return;
  const { gtmId, timer } = pendingGtm;
  pendingGtm = null;
  if (timer !== null && w) w.clearTimeout(timer);
  releaseGtm(gtmId);
}

function applyConsent(next: ConsentState): void {
  if (!state) return;
  const prev = state.consent;
  state.consent = next;
  const g = (v: boolean) => (v ? 'granted' : 'denied');
  safe(() => gtag('consent', 'update', {
    analytics_storage: g(next.analytics),
    ad_storage: g(next.marketing),
    ad_user_data: g(next.marketing),
    ad_personalization: g(next.marketing),
  }));
  const revoking = !!prev?.analytics && !next.analytics;
  if (revoking) {
    // Revogacao: 'consent false' so apaga cookies; medido, o Clarity continua
    // enviando /collect (cliques, DOM). 'stop' encerra a coleta de fato.
    clarity('consent', false);
    clarity('stop');
  }
  if (next.analytics && state.consentRevoked) clarity('start');
  if (next.analytics) state.consentRevoked = false;
  clarity('consentv2', { ad_Storage: g(next.marketing), analytics_Storage: g(next.analytics) });
  // So depois do consentv2 negado: a partir daqui nossos eventos param.
  if (prev?.analytics && !next.analytics && state.consentRequired) state.consentRevoked = true;
  relayConsentToChildren(next);
  maybeInject();
}

/** Iframes do proprio Turbine dentro deste documento (catalogo/grupo abrem o personalizador). */
function ownChildFrame(source: MessageEventSource | null, origin: string): boolean {
  if (!w || !source || origin !== w.location.origin) return false;
  try {
    const frames = document.querySelectorAll('iframe');
    for (let i = 0; i < frames.length; i++) if (frames[i].contentWindow === source) return true;
  } catch { /* noop */ }
  return false;
}

/** Repassa o consentimento recebido da loja aos iframes internos (mesma origem, so o booleano). */
function relayConsentToChildren(consent: ConsentState): void {
  if (!w) return;
  safe(() => {
    const frames = document.querySelectorAll('iframe');
    for (let i = 0; i < frames.length; i++) {
      try {
        const src = frames[i].getAttribute('src') || '';
        if (new URL(src, w.location.href).origin !== w.location.origin) continue;
        frames[i].contentWindow?.postMessage({ type: 'turbine:consent', analytics: consent.analytics, marketing: consent.marketing }, w.location.origin);
      } catch { /* noop */ }
    }
  });
}

function listenConsent(): void {
  if (!w || !embedded()) return;
  const nested = parentIsTurbine();
  w.addEventListener('message', (event: MessageEvent) => {
    safe(() => {
      const data = event.data as { type?: unknown } | null;
      // Um iframe interno pede o estado: responde se ja o conhecemos.
      if (data && typeof data === 'object' && data.type === 'turbine:consentRequest') {
        if (state?.consent && ownChildFrame(event.source, event.origin)) {
          (event.source as Window).postMessage({ type: 'turbine:consent', analytics: state.consent.analytics, marketing: state.consent.marketing }, event.origin);
        }
        return;
      }
      if (event.source !== w.parent) return;
      // Dentro do Turbine so o pai de MESMA origem vale; na loja, so as origens conhecidas.
      if (nested ? event.origin !== w.location.origin : !isConsentOrigin(event.origin)) return;
      const parsed = parseConsentMessage(event.data);
      if (parsed) applyConsent(parsed);
    });
  });
  requestConsent();
}

/**
 * Pede o estado de consentimento ao pai (loja, ou o proprio Turbine se aninhado).
 * A mensagem nao carrega dado nenhum; '*' so e usado sem referrer valido. Repetido
 * uma vez quando o GTM e liberado: a extensao da loja pode ter registrado o ouvinte
 * depois do primeiro pedido.
 */
function requestConsent(): void {
  if (!w || !embedded()) return;
  safe(() => {
    let target = '*';
    if (parentIsTurbine()) target = w.location.origin;
    else {
      try {
        const ref = document.referrer ? new URL(document.referrer).origin : '';
        if (isConsentOrigin(ref)) target = ref;
      } catch { /* noop */ }
    }
    w.parent.postMessage({ type: 'turbine:consentRequest' }, target);
  });
}

/** Chamado uma vez, em main.tsx, antes do React montar. */
export function initAnalytics(): void {
  if (!w || state) return;
  safe(() => {
    const decision = decideAnalytics({
      pathname: w.location.pathname,
      search: w.location.search,
      hostname: w.location.hostname,
      webdriver: !!(navigator as Navigator & { webdriver?: boolean }).webdriver,
      killSwitch: import.meta.env?.VITE_TURBINE_ANALYTICS ?? null,
      gtmIdFromEnv: import.meta.env?.VITE_TURBINE_GTM_ID ?? null,
    });
    const surface = surfaceFor(w.location.pathname, w.location.search);
    state = {
      enabled: decision.enabled,
      reason: decision.reason,
      gtmId: decision.gtmId,
      gtmRequested: false,
      gtmLoaded: false,
      gtmFailed: false,
      gtmMode: 'load',
      consentRequired: consentRequired(import.meta.env?.VITE_TURBINE_ANALYTICS_CONSENT ?? null),
      gtmReleased: false,
      gtmInjectedAt: null,
      buffered: 0,
      dropped: 0,
      stopped: false,
      consentRevoked: false,
      consent: null,
      surface,
      last: [],
    };
    w.__turbineAnalytics = state;
    if (!decision.enabled) return;
    const env = environmentFor(w.location.hostname, w.location.search);
    const load = resolveGtmLoadMode(surface, w.location.search, env);
    state.gtmMode = load.mode;
    if (load.off) { state.enabled = false; state.reason = 'medicao-off'; return; }

    // Consentimento NEGADO por padrao, antes de qualquer tag.
    w.dataLayer = w.dataLayer || [];
    gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      wait_for_update: 500,
    });

    // Fila do Clarity (o template oficial do GTM reaproveita `clarity.q`).
    if (typeof w.clarity !== 'function') {
      const q: Clarity = function (...args: unknown[]) { (q.q = q.q || []).push(args); } as Clarity;
      w.clarity = q;
    }
    clarity('consentv2', { ad_Storage: 'denied', analytics_Storage: 'denied' });

    const isEmbedded = embedded();
    const context = {
      turbine_surface: surface,
      turbine_env: environmentFor(w.location.hostname, w.location.search),
      turbine_entry: entryFor(isEmbedded, parentIsTurbine()),
      turbine_device: deviceFor(
        w.innerWidth || 0,
        !!w.matchMedia?.('(pointer: coarse)').matches,
      ),
    };
    for (const [k, v] of Object.entries(context)) clarity('set', k, v);
    const productMatch = w.location.pathname.match(/^\/customize\/(\d{4,20})/);
    if (productMatch) clarity('set', 'turbine_product', productMatch[1]);
    w.dataLayer.push({ event: 'turbine_context', ...context });

    listenConsent();
    scheduleGtm(decision.gtmId, load.mode);
  });
}

/** Evento de analytics (dataLayer + Clarity). Campos passam pela lista fechada. */
export function track(name: string, props?: Record<string, unknown>, errorCategory?: string): void {
  if (!w || !state?.enabled || state.stopped || state.consentRevoked) return;
  safe(() => {
    if (ONCE_PER_DOCUMENT.has(name)) {
      if (fired.has(name)) return;
      fired.add(name);
    }
    if (SKIP_FIRST_PER_DOCUMENT.has(name) && !fired.has(`seen:${name}`)) {
      fired.add(`seen:${name}`);
      return;
    }
    // Antes de o GTM existir os eventos ficam na fila (dataLayer / clarity.q): limite
    // pequeno, descarta o excedente, e nao ha timers nem processamento extra.
    if (!state!.gtmRequested) {
      if (state!.buffered >= MAX_BUFFERED_EVENTS) { state!.dropped++; return; }
      state!.buffered++;
    }
    const params = sanitizeProps(props);
    const dl = (w.dataLayer = w.dataLayer || []);
    // Zera os parametros antes: o modelo de dados do GTM mescla objetos e
    // deixaria campos de um evento vazarem para o proximo.
    dl.push({ event_params: null });
    dl.push({ event: name, event_params: params });
    clarity('event', name);
    if (errorCategory) {
      clarity('set', 'turbine_error', errorCategory);
      // Sessao com erro e a que mais interessa rever: prioriza a gravacao.
      clarity('upgrade', `erro:${errorCategory}`);
    }
    if (name === 'personalization_step_changed' && typeof params.step === 'string') {
      clarity('set', 'turbine_step', params.step);
    }
    state!.last.push({ name, t: Math.round(performance.now()) });
    if (state!.last.length > 30) state!.last.shift();
  });
}

/** Ponte do `trackEvent` interno do personalizador. */
export function trackTurbineEvent(eventType: string, data?: Record<string, unknown>): void {
  if (!state?.enabled) return;
  safe(() => {
    const mapped = mapTurbineEvent(eventType, data || {});
    if (mapped) track(mapped.name, mapped.props, mapped.errorCategory);
  });
}

/**
 * Identificador pseudonimo da sessao do personalizador (o `sessionId`
 * aleatorio do Turbine). Vira tag no Clarity para cruzar a gravacao com a
 * telemetria tecnica (eventos, Store Monitor, sonda de queda). Nunca e dado
 * pessoal e nunca e o `pacco_clid` da loja.
 */
export function setAnalyticsSession(sessionId: string | null | undefined): void {
  const id = String(sessionId || '');
  if (!/^[A-Za-z0-9_-]{6,80}$/.test(id)) return;
  if (fired.has(`session:${id}`)) return;
  fired.add(`session:${id}`);
  clarity('set', 'turbine_session', id);
}

/** Erro tecnico (do Store Monitor): so a categoria, limitada por documento. */
export function reportAnalyticsError(marker: string): void {
  if (!state?.enabled) return;
  safe(() => {
    const category = errorCategoryForMonitorMarker(marker);
    const n = (errorCounts.get(category) || 0) + 1;
    errorCounts.set(category, n);
    if (n > MAX_ERRORS_PER_CATEGORY) return;
    track('customizer_error', { category }, category);
  });
}

/**
 * Transicao SPA para uma rota nao publica (nao acontece no fluxo do cliente,
 * mas o roteador permite). O GTM nao pode ser descarregado; o Clarity e
 * parado e os eventos deixam de sair.
 */
export function onRouteChange(pathname: string): void {
  if (!state?.enabled || state.stopped || state.consentRevoked) return;
  if (isTrackablePath(pathname)) return;
  safe(() => {
    w?.clarity?.('consent', false);
    state!.stopped = true;
  });
}
